import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowRight, Check, Crown, Gift, Users, Calendar, Bell, Share2, Instagram } from 'lucide-react'

const INSTAGRAM_URL = 'https://www.instagram.com/theokoiya'
import { supabase } from '../lib/supabase'
import LaunchHeader from '../components/LaunchHeader'
import LaunchFooter from '../components/LaunchFooter'
import FallingPetals from '../components/FallingPetals'

const PERKS = [
  { icon:Crown,    title:'Founding Couple status',  desc:'A permanent badge on your portal — you were here first.' },
  { icon:Gift,     title:'A founding reward',        desc:'First 3 couples get Premium free for life. The next 22 lock in 30% off.' },
  { icon:Bell,     title:"Know the moment we're live", desc:"We'll let you know the moment we open, with early access before everyone else." },
]

const HIGHLIGHTS = [
  { icon:Calendar, label:'Every event in one place' },
  { icon:Users,    label:'Guests & RSVP' },
  { icon:Gift,     label:'Registry, aso ebi & vendors' },
]

const field = {
  width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem',
  padding:'0.7rem 0.9rem', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem',
  outline:'none', boxSizing:'border-box', background:'#fff', color:'#1A1A1A',
}
const label = { fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:700, color:'#6B6B6B', display:'block', margin:'0 0 0.3rem' }

import { useSeo } from '../lib/seo'

/* A phone showing a mini Okoiya wedding portal — the actual product, not just a
   framed photo. Cover is a brand image; floating cards sell live features. */
function PhoneMockup() {
  return (
    <div style={{ position:'relative', width:264, margin:'0 auto', animation:'wlFloat 6s ease-in-out infinite' }}>
      {/* Floating stat — top right */}
      <div style={{ position:'absolute', top:30, right:-30, zIndex:5, display:'flex', alignItems:'center', gap:'0.5rem', background:'#fff', borderRadius:'0.85rem', padding:'0.5rem 0.7rem', boxShadow:'0 12px 30px rgba(0,0,0,0.14)' }}>
        <span style={{ width:28, height:28, borderRadius:'50%', background:'rgba(155,175,155,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Check size={15} color="#7A917A"/>
        </span>
        <div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:800, fontSize:'0.8rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>248</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.58rem', color:'#9B9B9B', margin:'0.15rem 0 0', lineHeight:1 }}>RSVPs in</p>
        </div>
      </div>

      {/* Floating stat — bottom left */}
      <div style={{ position:'absolute', bottom:46, left:-34, zIndex:5, display:'flex', alignItems:'center', gap:'0.5rem', background:'#fff', borderRadius:'0.85rem', padding:'0.5rem 0.7rem', boxShadow:'0 12px 30px rgba(0,0,0,0.14)' }}>
        <span style={{ width:28, height:28, borderRadius:'50%', background:'rgba(196,130,106,0.16)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Gift size={15} color="#C4826A"/>
        </span>
        <div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:800, fontSize:'0.8rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>₦480,000</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.58rem', color:'#9B9B9B', margin:'0.15rem 0 0', lineHeight:1 }}>gifted</p>
        </div>
      </div>

      {/* Phone body */}
      <div style={{ position:'relative', background:'#15110F', borderRadius:40, padding:9, boxShadow:'0 34px 70px rgba(21,17,15,0.32), 0 10px 22px rgba(0,0,0,0.14)' }}>
        {/* Notch */}
        <div style={{ position:'absolute', top:16, left:'50%', transform:'translateX(-50%)', width:92, height:21, background:'#15110F', borderRadius:100, zIndex:3 }}/>
        {/* Screen */}
        <div style={{ borderRadius:32, overflow:'hidden', background:'#FAFAF8' }}>
          {/* Status bar */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.55rem 1.05rem 0.4rem' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:800, color:'#1A1A1A' }}>9:41</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
              <span style={{ display:'inline-flex', alignItems:'flex-end', gap:1.5 }}>
                {[5,7,9,11].map((h,i)=>(<span key={i} style={{ width:2.5, height:h, borderRadius:1, background:'#1A1A1A' }}/>))}
              </span>
              <span style={{ width:16, height:8, border:'1.2px solid #1A1A1A', borderRadius:2.5, position:'relative', display:'inline-block' }}>
                <span style={{ position:'absolute', top:1.5, bottom:1.5, left:1.5, width:9, background:'#1A1A1A', borderRadius:1 }}/>
              </span>
            </span>
          </div>

          {/* Cover photo + couple */}
          <div style={{ position:'relative', margin:'0 0.5rem', borderRadius:'1.1rem', overflow:'hidden', height:212 }}>
            <img src="/brand/regal-red-700.webp" alt="" loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(20,12,10,0.72) 4%, rgba(20,12,10,0) 52%)' }}/>
            <div style={{ position:'absolute', left:0, right:0, bottom:0, padding:'0.85rem 0.9rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(255,255,255,0.82)', margin:'0 0 0.25rem' }}>The wedding of</p>
              <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.3rem', color:'#fff', margin:0, lineHeight:1.05 }}>Amaka &amp; Emeka</p>
            </div>
          </div>

          {/* Date */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', padding:'0.75rem 0 0.6rem' }}>
            <Calendar size={11} color="#C4826A"/>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', fontWeight:700, color:'#6B6B6B' }}>Sat, 12 December 2026</span>
          </div>

          {/* Action pills */}
          <div style={{ display:'flex', gap:'0.45rem', padding:'0 0.65rem' }}>
            <span style={{ flex:1, textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontWeight:800, fontSize:'0.62rem', color:'#fff', background:'#C4826A', borderRadius:100, padding:'0.5rem 0' }}>RSVP</span>
            <span style={{ flex:1, textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontWeight:800, fontSize:'0.62rem', color:'#C4826A', background:'#fff', border:'1px solid rgba(196,130,106,0.4)', borderRadius:100, padding:'0.5rem 0' }}>Registry</span>
          </div>

          {/* Tiny countdown strip */}
          <div style={{ display:'flex', justifyContent:'center', gap:'0.3rem', padding:'0.75rem 0.65rem 0.95rem' }}>
            {[['142','d'],['08','h'],['24','m']].map(([v,u])=>(
              <span key={u} style={{ background:'#fff', border:'1px solid #EFE8E0', borderRadius:'0.55rem', padding:'0.35rem 0.5rem', fontFamily:'Gibs, serif', fontSize:'0.78rem', color:'#1A1A1A', display:'flex', alignItems:'baseline', gap:2 }}>
                {v}<span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.5rem', fontWeight:700, color:'#A89E94' }}>{u}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function WaitlistPage() {
  useSeo({ title: 'Join the Waitlist', description: 'Be one of the founding couples on Okoiya — early access and founding perks before everyone else.', path: '/waitlist' })
  const [form, setForm] = useState({
    name:'', partner_name:'', email:'', phone:'', location:'', country:'',
    wedding_date:'', guest_estimate:'', biggest_challenge:'', heard_from:'', wants_updates:true,
  })
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState('')
  const [done, setDone]       = useState(false)
  const [position, setPosition] = useState(null)
  const [tier, setTier]       = useState(null)
  const [count, setCount]     = useState(null)
  const [launchAt, setLaunchAt] = useState(null)
  const [now, setNow]           = useState(Date.now())
  const formRef = useRef(null)
  const location = useLocation()

  // Arriving from the header "Join the waitlist" CTA on another page (/waitlist#join)
  // → scroll to exactly where the form starts (offset for the floating header).
  useEffect(() => {
    if (location.hash !== '#join') return
    const t = setTimeout(() => {
      const el = formRef.current
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior:'smooth' })
    }, 150)
    return () => clearTimeout(t)
  }, [location.hash])

  useEffect(() => {
    supabase.rpc('waitlist_count').then(({ data }) => { if (typeof data === 'number') setCount(data) }, () => {})
    // Admin-set launch time (admin_settings is publicly readable). Value is an ISO string.
    supabase.from('admin_settings').select('value').eq('key', 'launch_at').maybeSingle()
      .then(({ data }) => { const v = data?.value; if (v) { const t = new Date(v).getTime(); if (!isNaN(t)) setLaunchAt(t) } }, () => {})
  }, [])

  // Tick the countdown every second once we have a launch time
  useEffect(() => {
    if (!launchAt) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [launchAt])

  const remaining     = launchAt ? Math.max(0, launchAt - now) : 0
  const showCountdown = !!launchAt && remaining > 0
  const cdSecs = Math.floor(remaining / 1000)
  const cd = { d: Math.floor(cdSecs / 86400), h: Math.floor((cdSecs % 86400) / 3600), m: Math.floor((cdSecs % 3600) / 60), s: cdSecs % 60 }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  async function submit(e) {
    e.preventDefault()
    setError('')
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in your name, email and WhatsApp number.'); return
    }
    // Open a blank tab NOW, inside the click gesture, so the browser doesn't treat
    // it as a blocked pop-up. We point it at Instagram once the signup succeeds
    // (or close it if something goes wrong). This sends them to follow us while the
    // original tab keeps their founding-couple confirmation.
    let igTab = null
    try { igTab = window.open('about:blank', '_blank') } catch { /* pop-ups blocked */ }
    setBusy(true)
    const payload = {
      ...form,
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      wedding_date: form.wedding_date || null,
    }
    let isNew = true
    const { error: err } = await supabase.from('waitlist_leads').insert(payload)
    if (err) {
      // 23505 = already on the list → still a win, but we don't re-rank them
      if (err.code === '23505' || /duplicate|unique/i.test(err.message || '')) {
        isNew = false
      } else {
        if (igTab) { try { igTab.close() } catch { /* noop */ } }
        setError("Something went wrong — please try again, or DM us on Instagram.")
        setBusy(false); return
      }
    }
    // Success → send the new tab to Instagram so they can follow us.
    if (igTab) { try { igTab.location.href = INSTAGRAM_URL } catch { /* noop */ } }
    setDone(true)
    // Refresh the count; for a fresh signup, their position == the new total,
    // which decides their founding tier (mirrors the server-side trigger).
    const { data: c } = await supabase.rpc('waitlist_count')
    if (typeof c === 'number') {
      setCount(c)
      if (isNew) {
        setPosition(c)
        setTier(c <= 3 ? 'lifetime' : c <= 25 ? 'discount' : 'standard')
      }
    }
    setBusy(false)
  }

  function share() {
    const url = window.location.origin + '/waitlist'
    const text = "I just joined the founding couples on Okoiya — the all-in-one way to plan a Nigerian wedding. Join me before the doors open 💍"
    if (navigator.share) {
      navigator.share({ title:'Okoiya — Founding Couples', text, url }).catch(()=>{})
    } else {
      navigator.clipboard?.writeText(`${text} ${url}`)
      alert('Link copied — paste it to your engaged friends 💌')
    }
  }

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior:'smooth', block:'center' })

  return (
    <div style={{ background:'#FAFAF8', minHeight:'100vh' }}>
      <LaunchHeader/>
      <style>{`
        @keyframes wlFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes wlPop { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:none} }
        @keyframes wlPing { 0%{transform:scale(1);opacity:0.55} 100%{transform:scale(2.8);opacity:0} }
        @media (max-width:860px){ .wl-grid{ grid-template-columns:1fr !important; gap:2.25rem !important; } .wl-form2{ grid-template-columns:1fr !important; } }
        @media (max-width:560px){
          .wl-hero{ padding:2.25rem 1.75rem 2.75rem !important; }
          .wl-h1{ font-size:1.95rem !important; line-height:1.28 !important; margin:1.4rem 0 1rem !important; }
          .wl-pitch{ text-align:center !important; }
          .wl-pitch > h1, .wl-pitch > p { margin-left:auto !important; margin-right:auto !important; }
          .wl-offer{ margin-left:auto !important; margin-right:auto !important; }
          .wl-highlights{ justify-content:center !important; }
        }
      `}</style>

      {/* ── HERO + FORM ── */}
      <div style={{ position:'relative', overflow:'hidden' }}>
        <FallingPetals/>
        <section className="wl-hero" style={{ maxWidth:1100, margin:'0 auto', padding:'3rem 1.5rem 3.5rem', position:'relative', zIndex:1 }}>
          {/* Top — pitch (spans full width above the showcase) */}
          <div className="wl-pitch" style={{ textAlign:'center', maxWidth:780, margin:'0 auto 2.75rem' }}>
            <h1 className="wl-h1" style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2.2rem,5.2vw,3.6rem)', lineHeight:1.08, color:'#1A1A1A', margin:'1.25rem 0 1rem' }}>
              The wedding planning you've been praying for is almost <em style={{ color:'#C4826A', fontStyle:'italic' }}>here</em>.
            </h1>

            <p className="wl-sub" style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1.05rem', color:'#6B6B6B', lineHeight:1.75, margin:'0 auto 1.5rem', maxWidth:520 }}>
              Your budget, guests, aso ebi, registry and vendors, finally in one beautiful place. Join the founding couples and be the first to know the moment we open.
            </p>

            {/* Offer chip */}
            <div className="wl-offer" style={{ display:'inline-flex', alignItems:'center', gap:'0.75rem', textAlign:'left', background:'linear-gradient(135deg,rgba(196,130,106,0.1),rgba(155,175,155,0.08))', border:'1px solid rgba(196,130,106,0.25)', borderRadius:'1rem', padding:'0.85rem 1rem', maxWidth:520 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(196,130,106,0.14)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Crown size={19} color="#C4826A"/>
              </div>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A', margin:0, lineHeight:1.55 }}>
                <strong>First 3 couples:</strong> Premium free, for life. <strong>The next 22:</strong> 30% off. Your spot is locked by where you join.
              </p>
            </div>

            <div style={{ marginTop:'1.6rem' }}>
              <button onClick={scrollToForm} style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.95rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.95rem 2rem', cursor:'pointer', boxShadow:'0 8px 22px rgba(196,130,106,0.28)' }}
                onMouseEnter={e=>e.currentTarget.style.background='#B36B55'} onMouseLeave={e=>e.currentTarget.style.background='#C4826A'}>
                Claim my founding spot <ArrowRight size={17}/>
              </button>
            </div>
          </div>

          <div className="wl-grid" style={{ display:'grid', gridTemplateColumns:'0.92fr 1.08fr', gap:'2.75rem', alignItems:'center' }}>

            {/* Left — product showcase (phone) */}
            <div className="wl-illus">
              <PhoneMockup/>

              {showCountdown && (
                <div style={{ marginTop:'1.75rem' }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'#C4826A', margin:'0 0 0.6rem', textAlign:'center' }}>⏳ Doors open in</p>
                  <div style={{ display:'flex', gap:'0.5rem', justifyContent:'center' }}>
                    {[['Days',cd.d],['Hours',cd.h],['Mins',cd.m],['Secs',cd.s]].map(([l,v])=>(
                      <div key={l} style={{ background:'#fff', border:'1px solid #E5E0D8', borderRadius:'0.875rem', padding:'0.6rem 0.5rem', textAlign:'center', minWidth:58, boxShadow:'0 2px 10px rgba(0,0,0,0.04)' }}>
                        <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.55rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{String(v).padStart(2,'0')}</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.58rem', fontWeight:700, color:'#9B9B9B', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0.25rem 0 0' }}>{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="wl-highlights" style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'1.25rem', marginTop:'1.75rem' }}>
                {HIGHLIGHTS.map(h => (
                  <span key={h.label} style={{ display:'inline-flex', alignItems:'center', gap:'0.45rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B' }}>
                    <h.icon size={15} color="#9BAF9B"/> {h.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — the form / success */}
            <div ref={formRef} id="join">
              <div style={{ background:'#fff', border:'1px solid #E5E0D8', borderRadius:'1.5rem', padding:'1.5rem', boxShadow:'0 12px 40px rgba(0,0,0,0.06)' }}>
                {done ? (
                  <div style={{ textAlign:'center', padding:'1rem 0.5rem', animation:'wlPop 0.4s ease-out' }}>
                    <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(155,175,155,0.18)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
                      <Check size={30} color="#7A917A"/>
                    </div>
                    <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.6rem', color:'#1A1A1A', margin:'0 0 0.4rem' }}>You're in. 💍</h2>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.92rem', color:'#6B6B6B', lineHeight:1.65, margin:'0 0 1rem' }}>
                      {tier === 'lifetime'
                        ? <>You're <strong style={{ color:'#C4826A' }}>founding couple #{position}</strong>, one of the first 3. <strong>Okoiya Premium is yours, free for life.</strong> We'll let you know the moment we open.</>
                        : tier === 'discount'
                        ? <>You're <strong style={{ color:'#C4826A' }}>founding couple #{position}</strong>. You made the first 25 and locked in <strong>30% off</strong> your wedding premium. We'll let you know the moment we open.</>
                        : position
                        ? <>You're <strong style={{ color:'#C4826A' }}>founding couple #{position}</strong> on the list. We'll let you know the moment we open.</>
                        : <>You're on the founding couples list. We'll let you know the moment we open.</>}
                    </p>

                    {/* Instagram follow — we auto-open it in a new tab on submit; this is the
                        fallback (and reassurance) if the browser blocked the pop-up. */}
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'white', textDecoration:'none', background:'linear-gradient(135deg,#C4826A,#B36B55)', border:'none', borderRadius:100, padding:'0.8rem 1.6rem', boxShadow:'0 8px 20px rgba(196,130,106,0.26)' }}>
                      <Instagram size={17}/> Follow us on Instagram
                    </a>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#9B9B9B', margin:'0.6rem 0 1.25rem', lineHeight:1.5 }}>
                      We opened Instagram in a new tab — follow along so you don't miss launch day.
                    </p>

                    <div style={{ background:'#FAFAF8', border:'1px solid #E5E0D8', borderRadius:'1rem', padding:'1rem', marginBottom:'1rem' }}>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', lineHeight:1.6, margin:'0 0 0.75rem' }}>
                        Know someone engaged? Share Okoiya — the more founding couples who join through you, the better.
                      </p>
                      <button onClick={share} style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#C4826A', background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:100, padding:'0.6rem 1.4rem', cursor:'pointer' }}>
                        <Share2 size={15}/> Share with a friend
                      </button>
                    </div>
                    <Link to="/" style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', textDecoration:'none' }}>← Back to home</Link>
                  </div>
                ) : (
                  <form onSubmit={submit}>
                    <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>Join the founding couples</h2>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:'0 0 1.25rem' }}>Takes 30 seconds. No card, no commitment.</p>

                    <div className="wl-form2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
                      <div><label style={label}>Your name *</label><input style={field} value={form.name} onChange={set('name')} placeholder="e.g. Amaka"/></div>
                      <div><label style={label}>Partner's name</label><input style={field} value={form.partner_name} onChange={set('partner_name')} placeholder="e.g. Emeka"/></div>
                    </div>

                    <div style={{ marginBottom:'0.75rem' }}>
                      <label style={label}>Email *</label>
                      <input style={field} type="email" value={form.email} onChange={set('email')} placeholder="you@email.com"/>
                    </div>

                    <div style={{ marginBottom:'0.75rem' }}>
                      <label style={label}>WhatsApp number *</label>
                      <input style={field} value={form.phone} onChange={set('phone')} placeholder="+234 …"/>
                    </div>

                    <div className="wl-form2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
                      <div><label style={label}>City</label><input style={field} value={form.location} onChange={set('location')} placeholder="e.g. Lagos"/></div>
                      <div><label style={label}>Country</label><input style={field} value={form.country} onChange={set('country')} placeholder="e.g. Nigeria / UK"/></div>
                    </div>

                    <div className="wl-form2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
                      <div><label style={label}>Wedding date</label><input style={field} type="date" value={form.wedding_date} onChange={set('wedding_date')}/></div>
                      <div>
                        <label style={label}>Guests (est.)</label>
                        <select style={field} value={form.guest_estimate} onChange={set('guest_estimate')}>
                          <option value="">Not sure</option>
                          <option>Under 100</option><option>100–300</option><option>300–600</option><option>600+</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ marginBottom:'0.75rem' }}>
                      <label style={label}>Your biggest wedding-planning headache?</label>
                      <input style={field} value={form.biggest_challenge} onChange={set('biggest_challenge')} placeholder="optional — helps us help you"/>
                    </div>

                    <div style={{ marginBottom:'1rem' }}>
                      <label style={label}>How did you hear about us?</label>
                      <select style={field} value={form.heard_from} onChange={set('heard_from')}>
                        <option value="">Select…</option>
                        <option>Instagram</option><option>TikTok</option><option>A friend</option>
                        <option>A vendor / planner</option><option>Google / search</option><option>Other</option>
                      </select>
                    </div>

                    {error && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#B91C1C', margin:'0 0 0.75rem' }}>{error}</p>}

                    <button type="submit" disabled={busy} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.95rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.95rem', cursor:busy?'default':'pointer', opacity:busy?0.6:1 }}>
                      {busy ? 'Saving your spot…' : <>Save my spot <ArrowRight size={17}/></>}
                    </button>

                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#BCBCBC', textAlign:'center', margin:'0.75rem 0 0', lineHeight:1.5 }}>
                      We'll only message you about your founding access. No spam, ever.
                    </p>
                  </form>
                )}
              </div>

              {count != null && count >= 25 && !done && (
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#9B9B9B', textAlign:'center', margin:'0.875rem 0 0' }}>
                  <strong style={{ color:'#C4826A' }}>{count.toLocaleString()}</strong> couples have already joined the list ✨
                </p>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ── FOUNDING PERKS ── */}
      <section style={{ maxWidth:1000, margin:'0 auto', padding:'1rem 1.5rem 4rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:800, letterSpacing:'0.12em', textTransform:'uppercase', color:'#C4826A', textAlign:'center', margin:'0 0 0.5rem' }}>Why join now</p>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.6rem,3.5vw,2.2rem)', color:'#1A1A1A', textAlign:'center', margin:'0 0 2.25rem' }}>Founding couples get the royal treatment</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:'1rem' }}>
          {PERKS.map(p => (
            <div key={p.title} style={{ background:'#fff', border:'1px solid #E5E0D8', borderRadius:'1.25rem', padding:'1.5rem' }}>
              <div style={{ width:46, height:46, borderRadius:'0.875rem', background:'rgba(196,130,106,0.1)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.875rem' }}>
                <p.icon size={21} color="#C4826A"/>
              </div>
              <h3 style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'1rem', color:'#1A1A1A', margin:'0 0 0.4rem' }}>{p.title}</h3>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#6B6B6B', lineHeight:1.65, margin:0 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign:'center', marginTop:'2.5rem' }}>
          <button onClick={scrollToForm} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.95rem', color:'white', background:'#1A1A1A', border:'none', borderRadius:100, padding:'0.95rem 2.25rem', cursor:'pointer' }}>
            Save my founding spot
          </button>
        </div>
      </section>

      <LaunchFooter/>
    </div>
  )
}
